// Small, pure selectors over the raw OMS `recommendations` array attached to each row.
// Callers (TableRow, RecommendationTable, utils.matchesStrategyGroup) assemble these
// into whatever shape they need at the point of display.

export type SuggestionMap = Record<string, { trade_type?: string | null; [key: string]: unknown }>;

export interface RawRecommendation {
  recommended_by?: string | null;
  recommendation_type?: string | null;
  target_weight?: number | null;
  target_price?: number | null;
  decision_price?: number | null;
  target_date?: string | null;
  decision?: string | null;
  suggestions?: SuggestionMap | null;
  assumption_years?: number | null;
  created_ts?: string | null;
}

/** True when a call was given by someone who no longer holds the company in that role.
 *  Both screens that surface this compare the same two names — the author of the call and
 *  the current assignment — so the rule lives here rather than in either of them. */
export const isHandedOnCall = (
  authorName: string | null | undefined,
  currentOwnerName: string | null | undefined,
): boolean => Boolean(authorName && currentOwnerName && authorName !== currentOwnerName);

/** True when a suggestions map has at least one entry with a non-null trade_type. */
export const hasActionableSuggestion = (
  suggestions: SuggestionMap | null | undefined,
): boolean => suggestions != null && Object.values(suggestions).some((s) => s?.trade_type != null);

const MS_PER_DAY = 86_400_000;

/** The horizon a rec applies to. Analyst/IC rows written before the five-year view
 *  existed carry no assumption_years, so an absent value means three years. */
export const recHorizon = (rec: Pick<RawRecommendation, "assumption_years">): number =>
  rec.assumption_years ?? 3;

/** Annualised implied return %, rounded to 2 dp. Null on missing/invalid inputs. */
export const computeImpliedReturn = (
  targetPrice: number | null | undefined,
  decisionPrice: number | null | undefined,
  assumptionYears: number | null | undefined,
): number | null => {
  if (!targetPrice || !decisionPrice || !assumptionYears) return null;
  if (targetPrice <= 0 || decisionPrice <= 0 || assumptionYears <= 0) return null;
  const raw = (Math.pow(targetPrice / decisionPrice, 1 / assumptionYears) - 1) * 100;
  return Number.isFinite(raw) ? Math.round(raw * 100) / 100 : null;
};

// Parse "YYYY-MM-DD..." → epoch ms at UTC midnight (mirrors Python date.fromisoformat).
const parseDateOnly = (s: string | null | undefined): number | null => {
  if (!s) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s));
  if (!match) return null;
  const ts = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isFinite(ts) ? ts : null;
};

/** Years from refDate (UTC date-only) to target_date. Null if in the past or invalid. */
export const yearsToTarget = (targetDate: string | null | undefined, refDate?: Date): number | null => {
  const targetMs = parseDateOnly(targetDate);
  if (targetMs === null) return null;
  const ref = refDate ?? new Date();
  const refMs = Date.UTC(ref.getFullYear(), ref.getMonth(), ref.getDate());
  const years = (targetMs - refMs) / (MS_PER_DAY * 365.25);
  return years > 0 ? years : null;
};

/** First preliminary/approved rec where recommended_by === name, at one horizon.
 *  Preliminaries take priority.
 *
 *  `assumptionYears` is required rather than defaulted: raw now exports a three-year and
 *  a five-year row per author, so a horizon-blind lookup would return whichever the array
 *  happened to list first. */
export const findMatchedRec = (
  recs: RawRecommendation[] | null | undefined,
  name: string | null | undefined,
  assumptionYears: number,
): RawRecommendation | null => {
  if (!name || !Array.isArray(recs) || recs.length === 0) return null;
  let approvedMatch: RawRecommendation | null = null;
  for (const rec of recs) {
    if (rec.recommended_by !== name) continue;
    if (recHorizon(rec) !== assumptionYears) continue;
    const type = (rec.recommendation_type ?? "").toLowerCase();
    if (type === "preliminary") return rec;
    if (type === "approved" && approvedMatch === null) approvedMatch = rec;
  }
  return approvedMatch;
};

/** Latest (by created_ts) model rec whose recommended_by contains `label` at the given horizon. */
export const findLatestModel = (
  recs: RawRecommendation[] | null | undefined,
  label: string,
  assumptionYears: number,
): RawRecommendation | null => {
  if (!Array.isArray(recs) || recs.length === 0) return null;
  const labelLower = label.toLowerCase();
  let best: RawRecommendation | null = null;
  let bestKey = "";
  for (const rec of recs) {
    if ((rec.recommendation_type ?? "").toLowerCase() !== "model") continue;
    if (!(rec.recommended_by ?? "").toLowerCase().includes(labelLower)) continue;
    if (recHorizon(rec) !== assumptionYears) continue;
    const key = rec.created_ts ?? "";
    if (best === null || key > bestKey) {
      best = rec;
      bestKey = key;
    }
  }
  return best;
};

/** For analyst/IC: the price used to compute implied return.
 *  "Reduce above ..." decisions price IR off decision_price; everything else off target_price. */
export const priceForIr = (rec: RawRecommendation | null): number | null => {
  if (!rec) return null;
  const isReduceAbove = (rec.decision ?? "").toLowerCase().includes("reduce above");
  return (isReduceAbove ? rec.decision_price : rec.target_price) ?? null;
};

/** The model's view at one horizon, assembled from its Target Price and Buy Below recs.
 *
 *  Target Price wins where both carry a value; Buy Below fills in for a company the model
 *  has priced but not targeted. Kept here rather than at a call site so every screen
 *  reading a model target — the recommendations table, the block trade page — resolves the
 *  same rec and quotes the same implied return. */
export interface ModelTargetAtHorizon {
  targetPrice: number | null;
  targetDate: string | null;
  decision: string | null;
  buyBelow: number | null;
  targetPriceRec: RawRecommendation | null;
  buyBelowRec: RawRecommendation | null;
}

export const modelTargetAtHorizon = (
  recs: RawRecommendation[] | null | undefined,
  assumptionYears: number,
): ModelTargetAtHorizon => {
  const targetPriceRec = findLatestModel(recs, "Model Target Price", assumptionYears);
  const buyBelowRec = findLatestModel(recs, "Model Buy Below", assumptionYears);
  return {
    targetPrice: targetPriceRec?.target_price ?? buyBelowRec?.target_price ?? null,
    targetDate: targetPriceRec?.target_date ?? buyBelowRec?.target_date ?? null,
    decision: targetPriceRec?.decision ?? buyBelowRec?.decision ?? null,
    buyBelow: buyBelowRec?.decision_price ?? null,
    targetPriceRec,
    buyBelowRec,
  };
};

/** True iff recs contains any model rec (Target Price or Buy Below) at the given horizon. */
export const hasModelAtHorizon = (
  recs: RawRecommendation[] | null | undefined,
  assumptionYears: number,
): boolean =>
  findLatestModel(recs, "Model Target Price", assumptionYears) != null ||
  findLatestModel(recs, "Model Buy Below", assumptionYears) != null;

/** True iff an analyst or IC has written a rec (preliminary or approved) at the given horizon. */
export const hasAuthorRecAtHorizon = (
  recs: RawRecommendation[] | null | undefined,
  assumptionYears: number,
): boolean =>
  (recs ?? []).some((rec) => {
    const type = (rec.recommendation_type ?? "").toLowerCase();
    return (type === "preliminary" || type === "approved") && recHorizon(rec) === assumptionYears;
  });

/** True iff ANY author — model, analyst or IC — holds a view at the given horizon.
 *
 *  This, not hasModelAtHorizon, is what decides whether a strategy group has something to
 *  show: the LHF row is driven by the five-year view and the Core row by the three-year one,
 *  and TableRow renders the Analyst / IC columns at the row's own horizon regardless of
 *  whether the model has priced that horizon. Gating on the model alone hid a row whose
 *  Analyst and IC cells were populated and whose suggestion had already triggered. */
export const hasViewAtHorizon = (
  recs: RawRecommendation[] | null | undefined,
  assumptionYears: number,
): boolean =>
  hasModelAtHorizon(recs, assumptionYears) || hasAuthorRecAtHorizon(recs, assumptionYears);
